import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Label, Text, useDebouncedFn } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { PaymentsRoutingNumberType } from "#graphql/supergraphEnums";
import { useValidateRoutingNumberLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../../common/components/Form/Layout/SimpleFormRow";
import { useConsolidatedPaymentPrefContext } from "../../../Context";
import { useGenericBankValidation } from "../../../hooks/use-generic-bank-validation";
import { BankCountryMismatchWarning } from "../../BankCountryMismatchWarning";
import {
  BecsPaymentPrefInputFields,
  BecsPaymentPrefInputSchema,
  type BecsPaymentPrefInputType,
} from "../constants";
import { stripBsbDash } from "../utils";

export const BecsBankDetails: React.FC = () => {
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);
  const [bsbCodeField] = useField<string>(BecsPaymentPrefInputFields.BsbCode);
  const { selectedBankCountry } = useConsolidatedPaymentPrefContext();

  const { handleClear, handleValidateCompleted } =
    useGenericBankValidation<BecsPaymentPrefInputType>({
      nameField: BecsPaymentPrefInputFields.BankName,
      validatedCountryField: BecsPaymentPrefInputFields.BankCountryValidatedCountry,
    });

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data) => {
      const currentBsbCode = stripBsbDash(bsbCodeField.value ?? "");
      const responseBsbCode = stripBsbDash(data?.validateRoutingNumber?.routingNumber ?? "");

      if (currentBsbCode !== responseBsbCode) {
        return;
      }

      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};
      handleValidateCompleted({ setShowBankValidatorError, bankName, bankAddress, isValid });
    },
    context: clientContext(),
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidateRoutingNumber = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    const stripped = stripBsbDash(bsbCodeField.value ?? "");

    if (!stripped) {
      handleClear();
      setShowBankValidatorError(false);
      return;
    }

    const isValid = BecsPaymentPrefInputSchema.pick([
      BecsPaymentPrefInputFields.BsbCode,
    ]).isValidSync({
      [BecsPaymentPrefInputFields.BsbCode]: bsbCodeField.value,
    });

    if (isValid) {
      debouncedValidateRoutingNumber({
        variables: {
          validateRoutingNumberInput: {
            routingNumber: stripped,
            routingNumberType: PaymentsRoutingNumberType.AuBsb,
          },
        },
      });
    } else {
      handleClear();
      setShowBankValidatorError(false);
    }
  }, [bsbCodeField.value, debouncedValidateRoutingNumber, handleClear]);

  return (
    <FieldGroup title="Bank details">
      <SimpleFormRow>
        <Flex flexDirection="column">
          <Label>Bank country</Label>
          <Text variant="regular-md">Australia</Text>
        </Flex>
      </SimpleFormRow>
      <MultiInputFormRow>
        <Flex flexDirection="column">
          <FormTextInput
            label={
              <HelpIndicator
                id="bsb-code-help-indicator"
                placement="right"
                content="A BSB (Bank State Branch) code is a 6-digit number that identifies your bank and branch in Australia, often formatted as XXX-XXX."
              >
                <Text>
                  BSB code <Icon type="question-circle" size="small" variant="outline" />
                </Text>
              </HelpIndicator>
            }
            isFullWidth
            name={BecsPaymentPrefInputFields.BsbCode}
            placeholder="e.g. 123-456"
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized BSB code</ErrorMessage>}
        </Flex>
        <FormTextInput
          label="Bank name"
          isFullWidth
          name={BecsPaymentPrefInputFields.BankName}
        />
      </MultiInputFormRow>
      <BankCountryMismatchWarning
        bankCountryName={selectedBankCountry?.name}
        validatedCountryField={BecsPaymentPrefInputFields.BankCountryValidatedCountry}
      />
    </FieldGroup>
  );
};
